import { showErrorMessage, showSuccessMessage } from 'modules/alert/utils';
import { NOTIFICATION } from 'modules/alert/constants';
import { handleApiCall } from 'utils/helpers';
import { IGetDrawingResponse, IGetBomResponse } from 'modules/billOfMaterials/types';
import { IReferences } from 'modules/threads/types';
import {
  useCreateDrawingMutation,
  useDeleteDrawingMutation,
  useLazyGetBomQuery,
  useLazyGetDrawingQuery,
} from 'modules/billOfMaterials/bomApi';
import { useLazyGetReferenceQuery } from 'modules/threads/threadsApi';

type CreateDrawingTrigger = ReturnType<typeof useCreateDrawingMutation>[0];
type DeleteDrawingTrigger = ReturnType<typeof useDeleteDrawingMutation>[0];

type THandleDrawing = {
  drawing: boolean;
  referenceDrawing?: IGetDrawingResponse | null;
  createdReference: IReferences | null;
  createDrawing: CreateDrawingTrigger;
  deleteDrawing: DeleteDrawingTrigger;
  showSuccessMessage?: boolean;
  showErrorMessage?: boolean;
};

export const handleDrawing = async ({
  drawing,
  referenceDrawing,
  createdReference,
  createDrawing,
  deleteDrawing,
  showSuccessMessage: showSuccess = true,
  showErrorMessage: showError = true,
}: THandleDrawing) => {
  if (!createdReference?.id) return;

  if (drawing && !referenceDrawing) {
    const payload = {
      reference: Number(createdReference.id),
      drawing_number: '',
      part_number: '',
      title: createdReference.name,
      revision: 'A',
      drawing_type: 'COMPONENT',
      metadata: {
        description: createdReference.description || '',
      },
    };

    const res = await createDrawing(payload);

    handleApiCall(
      res,
      () => showError && showErrorMessage(NOTIFICATION.DRAWING_ADD_FAILURE),
      () => showSuccess && showSuccessMessage(NOTIFICATION.DRAWING_ADD_SUCCESS)
    );
    return;
  }

  if (!drawing && referenceDrawing?.id) {
    const res = await deleteDrawing(referenceDrawing.id);

    handleApiCall(
      res,
      () => showError && showErrorMessage(NOTIFICATION.DRAWING_DELETE_FAILURE),
      () => showSuccess && showSuccessMessage(NOTIFICATION.DRAWING_DELETE_SUCCESS)
    );
  }
};

export const fetchBomGroup = async (
  bomId: number,
  triggerGetBom: ReturnType<typeof useLazyGetBomQuery>[0],
  triggerGetDrawing: ReturnType<typeof useLazyGetDrawingQuery>[0],
  triggerGetReference: ReturnType<typeof useLazyGetReferenceQuery>[0]
): Promise<{
  bom: IGetBomResponse;
  subBoms: Record<number, IGetBomResponse>;
  drawings: Record<number, IGetDrawingResponse>;
  references: Record<number, IReferences>;
}> => {
  const subBoms: Record<number, IGetBomResponse> = {};
  const drawings: Record<number, IGetDrawingResponse> = {};
  const references: Record<number, IReferences> = {};

  const bom = await triggerGetBom(bomId).unwrap();

  const subBomIds = bom.items
    .map((item) => item.sub_assembly_bom)
    .filter((id): id is number => Boolean(id));

  await Promise.all(
    subBomIds.map(async (id) => {
      const result = await triggerGetBom(id).unwrap();
      subBoms[id] = result;
    })
  );

  const drawingIds = [
    ...(bom.items.map((item) => item.drawing).filter(Boolean) as number[]),
    ...Object.values(subBoms).flatMap((sub) =>
      sub.items.map((item) => item.drawing).filter(Boolean)
    ),
  ];

  await Promise.all(
    drawingIds.map(async (id) => {
      if (!id) return;
      const result = await triggerGetDrawing(id).unwrap();
      drawings[id] = result;
    })
  );

  const referenceIds = Object.values(drawings)
    .map((drawing) => drawing.reference)
    .filter(Boolean) as number[];

  await Promise.all(
    referenceIds.map(async (id) => {
      const result = await triggerGetReference(id).unwrap();
      references[id] = result;
    })
  );

  return { bom, subBoms, drawings, references };
};
